/**
 * Password Validation - Shared Client/Server Logic
 *
 * SECURITY FIX (Phase 3 #1): Synced with server-side validation
 * Ensures client and server enforce identical password requirements
 */

// Password validation configuration (matches server)
export const PASSWORD_CONFIG = {
  minLength: 12,
  maxLength: 128,
} as const;

// Password requirements patterns (matches server)
export const PASSWORD_PATTERNS = {
  uppercase: /[A-Z]/,
  lowercase: /[a-z]/,
  number: /[0-9]/,
  special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/,
} as const;

// Common passwords to block (subset for client-side)
const COMMON_PASSWORDS = new Set([
  'password123',
  'qwerty123456',
  '123456789012',
  'letmein12345',
  'welcome12345',
  'admin1234567',
  'iloveyou1234',
  'sunshine1234',
  'princess1234',
  'football1234',
  'monkey123456',
  'shadow123456',
  'master123456',
  'dragon123456',
]);

export interface PasswordValidationResult {
  valid: boolean;
  errors: string[];
  strength: 'weak' | 'fair' | 'good' | 'strong';
  score: number; // 0-100
}

export interface PasswordRequirement {
  label: string;
  met: boolean;
  category: 'length' | 'character' | 'quality';
}

/**
 * Validate password strength and requirements
 * SYNCED WITH: functions/lib/auth/password.ts
 */
export function validatePassword(password: string): PasswordValidationResult {
  const errors: string[] = [];
  let score = 0;

  // Length check
  if (password.length < PASSWORD_CONFIG.minLength) {
    errors.push(`Password must be at least ${PASSWORD_CONFIG.minLength} characters`);
  } else {
    score += 20;
    // Bonus for longer passwords
    if (password.length >= 16) score += 10;
    if (password.length >= 20) score += 10;
  }

  if (password.length > PASSWORD_CONFIG.maxLength) {
    errors.push(`Password must be at most ${PASSWORD_CONFIG.maxLength} characters`);
  }

  // Character class checks
  if (!PASSWORD_PATTERNS.uppercase.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  } else {
    score += 15;
  }

  if (!PASSWORD_PATTERNS.lowercase.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  } else {
    score += 15;
  }

  if (!PASSWORD_PATTERNS.number.test(password)) {
    errors.push('Password must contain at least one number');
  } else {
    score += 15;
  }

  if (!PASSWORD_PATTERNS.special.test(password)) {
    errors.push('Password must contain at least one special character');
  } else {
    score += 15;
  }

  // Common password check
  if (COMMON_PASSWORDS.has(password.toLowerCase())) {
    errors.push('This password is too common. Please choose a more unique password');
    score = Math.max(0, score - 30);
  }

  // Sequential/repeated characters check
  if (/(.)\1{2,}/.test(password)) {
    errors.push('Password should not contain more than 2 repeated characters');
    score = Math.max(0, score - 10);
  }

  if (/(?:abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|012|123|234|345|456|567|678|789)/i.test(password)) {
    score = Math.max(0, score - 10);
  }

  // Determine strength level
  let strength: PasswordValidationResult['strength'];
  if (score < 30) {
    strength = 'weak';
  } else if (score < 50) {
    strength = 'fair';
  } else if (score < 75) {
    strength = 'good';
  } else {
    strength = 'strong';
  }

  return {
    valid: errors.length === 0,
    errors,
    strength,
    score: Math.min(100, score),
  };
}

/**
 * Get password requirements with met status
 */
export function getPasswordRequirements(password: string): PasswordRequirement[] {
  return [
    {
      label: `At least ${PASSWORD_CONFIG.minLength} characters`,
      met: password.length >= PASSWORD_CONFIG.minLength,
      category: 'length',
    },
    {
      label: 'Contains uppercase letter (A-Z)',
      met: PASSWORD_PATTERNS.uppercase.test(password),
      category: 'character',
    },
    {
      label: 'Contains lowercase letter (a-z)',
      met: PASSWORD_PATTERNS.lowercase.test(password),
      category: 'character',
    },
    {
      label: 'Contains a number (0-9)',
      met: PASSWORD_PATTERNS.number.test(password),
      category: 'character',
    },
    {
      label: 'Contains special character (!@#$...)',
      met: PASSWORD_PATTERNS.special.test(password),
      category: 'character',
    },
    {
      label: 'Not a common password',
      met: !COMMON_PASSWORDS.has(password.toLowerCase()),
      category: 'quality',
    },
    {
      label: 'No repeated characters (aaa)',
      met: !/(.)\1{2,}/.test(password),
      category: 'quality',
    },
  ];
}

/**
 * Get password strength color for UI
 */
export function getPasswordStrengthColor(strength: PasswordValidationResult['strength']): string {
  switch (strength) {
    case 'weak':
      return 'bg-red-500';
    case 'fair':
      return 'bg-orange-500';
    case 'good':
      return 'bg-yellow-500';
    case 'strong':
      return 'bg-green-500';
  }
}

/**
 * Get password strength text color for UI
 */
export function getPasswordStrengthTextColor(strength: PasswordValidationResult['strength']): string {
  switch (strength) {
    case 'weak':
      return 'text-red-600';
    case 'fair':
      return 'text-orange-600';
    case 'good':
      return 'text-yellow-600';
    case 'strong':
      return 'text-green-600';
  }
}
